import { createReducer } from 'deox';
import env from '@/env';
import { Entity, Paging, EntityComparison, AppsInfo, ApisInfo, Country } from '@/types';
import {
  fetchEntitiesActions,
  fetchEntityBySubdomainActions,
  fetchEntityInvitationActions,
  loadMoreEntitiesActions,
  fetchEntitiesComparisonActions,
  loadMoreEntitiesComparisonActions,
  fetchEntityByIdActions,
  fetchEntityViewOnlyActions,
  fetchEntityDashboardActions,
  deleteEntityActions,
  fetchEntitiesDirectoryActions,
  fetchCountriesEntityActions,
  fetchFeaturedEntitiesActions,
  clearEntitiesActions,
  fetchRelatedEntitiesActions,
} from '@/redux/actions/service-directory/entity';
import { fetchAppsInfoActions } from '@/redux/actions/service-directory/app-info';
import { fetchApisInfoActions } from '@/redux/actions/service-directory/api-info';

export interface EntityState {
  appsInfo: AppsInfo;
  apisInfo: ApisInfo;
  groups: string[];
  featured: {
    data?: Entity[];
  };
  related: {
    data?: Entity[];
  };
  entities: {
    data?: Entity[];
    paging: Paging;
    keyword: string;
  };
  entitiesDirectory: {
    data?: Entity[];
    paging: Paging;
  };
  entity?: Entity;
  entityToJoin?: Entity;
  entityViewOnly?: Entity;
  entityDashboard?: Entity;
  entityBySubdomain?: Entity;
  entityById?: Entity;
  entitiesComparison: {
    data?: EntityComparison[];
    paging: Paging;
  };
  countries: {
    data?: {
      label: any;
      value: string;
      flagCI: string;
      flagRE: string;
      region: string;
      entityCount: number;
    }[];
    paging: Paging;
    meta: {
      authors: string[];
    };
  };
  requestEntityDetail: number;
}

const initialState: EntityState = {
  featured: {
    data: undefined,
  },
  related: {
    data: undefined,
  },
  entities: {
    data: undefined,
    paging: {
      pageNumber: 1,
      pageSize: 0,
      totalRecords: 0,
    },
    keyword: '',
  },
  entitiesDirectory: {
    data: undefined,
    paging: {
      pageNumber: 1,
      pageSize: 0,
      totalRecords: 0,
    },
  },
  entity: undefined,
  entityToJoin: undefined,
  entityViewOnly: undefined,
  entityDashboard: undefined,
  entityBySubdomain: undefined,
  entityById: undefined,
  entitiesComparison: {
    data: undefined,
    paging: {
      pageNumber: 1,
      pageSize: 0,
      totalRecords: 0,
    },
  },
  appsInfo: [],
  apisInfo: [],
  groups: [],
  countries: {
    data: undefined,
    paging: {
      pageNumber: 0,
      pageSize: 0,
      totalRecords: 0,
    },
    meta: {
      authors: [],
    },
  },
  requestEntityDetail: 0,
};

const serviceDirectoryEntityReducer = createReducer(initialState, (handleAction) => [
  handleAction(clearEntitiesActions.request, (state) => {
    return {
      ...state,
      entities: initialState.entities,
    };
  }),
  handleAction(fetchEntitiesActions.success, (state, { payload }) => {
    return {
      ...state,
      entities: payload,
    };
  }),
  handleAction(fetchFeaturedEntitiesActions.success, (state, { payload }) => {
    return {
      ...state,
      featured: payload,
    };
  }),
  handleAction(fetchEntitiesDirectoryActions.success, (state, { payload }) => {
    return {
      ...state,
      entitiesDirectory: payload,
    };
  }),
  handleAction(loadMoreEntitiesActions.success, (state, { payload }) => {
    const { data, paging } = payload;
    const newData = state.entities.data ? [...state.entities.data, ...data] : data;
    const newPayload = {
      data: newData,
      paging,
      keyword: state.entities.keyword,
    };
    return {
      ...state,
      entities: newPayload,
    };
  }),
  handleAction(fetchEntityBySubdomainActions.success, (state, { payload }) => {
    const { entity } = payload;
    return {
      ...state,
      entityBySubdomain: entity,
    };
  }),
  handleAction(fetchEntityByIdActions.success, (state, { payload }) => {
    const { entity } = payload;
    return {
      ...state,
      entityById: entity,
    };
  }),
  handleAction(fetchEntityViewOnlyActions.success, (state, { payload }) => {
    const { entity } = payload;
    return {
      ...state,
      entityViewOnly: entity,
      requestEntityDetail: state.requestEntityDetail + 1,
    };
  }),
  handleAction(fetchEntityViewOnlyActions.reset, (state) => {
    return {
      ...state,
      entityViewOnly: initialState.entityViewOnly,
    };
  }),
  handleAction(fetchEntityDashboardActions.success, (state, { payload }) => {
    const { entity } = payload;
    return {
      ...state,
      entityDashboard: entity,
    };
  }),
  handleAction(fetchEntityInvitationActions.success, (state, { payload }) => ({
    ...state,
    entityToJoin: payload.entity,
  })),
  handleAction(fetchEntitiesComparisonActions.success, (state, { payload }) => {
    return {
      ...state,
      entitiesComparison: payload,
    };
  }),
  handleAction(fetchEntitiesComparisonActions.reset, (state) => {
    return {
      ...state,
      entitiesComparison: initialState.entitiesComparison,
    };
  }),
  handleAction(fetchAppsInfoActions.success, (state, { payload }) => {
    return {
      ...state,
      appsInfo: payload,
    };
  }),
  handleAction(fetchAppsInfoActions.reset, (state) => {
    return {
      ...state,
      appsInfo: initialState.appsInfo,
    };
  }),
  handleAction(fetchApisInfoActions.success, (state, { payload }) => {
    return {
      ...state,
      groups: payload.groups,
      apisInfo: payload.apis,
    };
  }),
  handleAction(fetchApisInfoActions.reset, (state) => {
    return {
      ...state,
      groups: initialState.groups,
      apisInfo: initialState.apisInfo,
    };
  }),
  handleAction(loadMoreEntitiesComparisonActions.success, (state, { payload }) => {
    const { data, paging } = payload;
    const newData = state.entitiesComparison.data ? [...state.entitiesComparison.data, ...data] : data;
    const newPayload = {
      data: newData,
      paging,
    };
    return {
      ...state,
      entitiesComparison: newPayload,
    };
  }),
  handleAction(deleteEntityActions.success, (state, { payload }) => ({
    ...state,
    entitiesDirectory: {
      ...state.entitiesDirectory,
      data: state?.entitiesDirectory?.data?.filter((e) => e.id !== payload.entityId),
    },
  })),
  handleAction(fetchCountriesEntityActions.success, (state, { payload }) => {
    const { data, paging, meta } = payload;
    const beautifyCountries = data.map((country: Country) => ({
      label: country.attributes.name,
      value: country.attributes.code2,
      flagCI: `${env.api.baseUrl.staticAssets}/${country.attributes.flagUrlRound}_92.png`,
      flagRE: `${env.api.baseUrl.staticAssets}/${country.attributes.flagUrlRect}_92.png`,
      region: country.attributes.region,
      entityCount: country.attributes.entityCount,
    }));
    return {
      ...state,
      countries: {
        data: beautifyCountries,
        paging,
        meta,
      },
    };
  }),
  handleAction(fetchCountriesEntityActions.reset, (state) => {
    return {
      ...state,
      countries: initialState.countries,
    };
  }),
  handleAction(fetchRelatedEntitiesActions.success, (state, { payload }) => {
    return {
      ...state,
      related: payload,
    };
  }),
]);

export default serviceDirectoryEntityReducer;
